import { toast } from 'sonner';

import { getApiErrorMessage } from '@shared/utils/api-error';

import {
  useConfirmPdfMutation,
  useLoadProductsMutation,
  useStartSessionMutation,
  useFinishSessionMutation,
  useStoreProductsMutation,
  useRemoveProductMutation,
  useConfirmExportMutation,
  useDownloadExportMutation,
  useFetchSessionPdfsMutation,
  useFetchCustomizationPdfMutation,
} from '@features/customization-session/api/customization-session-api';

/** The actions of a session. An error is reported to the operator; `busy` locks the controls meanwhile. */
export const useSessionActions = (id: number) => {
  const [loadProducts, { isLoading: isLoadingProducts }] = useLoadProductsMutation();
  const [startSession, { isLoading: isStarting }] = useStartSessionMutation();
  const [finishSession, { isLoading: isFinishing }] = useFinishSessionMutation();
  const [storeProducts, { isLoading: isStoring }] = useStoreProductsMutation();
  const [removeProduct, { isLoading: isRemoving }] = useRemoveProductMutation();

  const [fetchSessionPdfs, { isLoading: isFetchingPdfs }] = useFetchSessionPdfsMutation();
  const [fetchCustomizationPdf, { isLoading: isFetchingPdf }] = useFetchCustomizationPdfMutation();
  const [confirmPdf, { isLoading: isConfirmingPdf }] = useConfirmPdfMutation();
  const [confirmExport, { isLoading: isConfirmingExport }] = useConfirmExportMutation();
  const [downloadExport, { isLoading: isDownloading }] = useDownloadExportMutation();

  const run = async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (exception) {
      toast.error(getApiErrorMessage(exception));
    }
  };

  return {
    busy: isLoadingProducts || isStarting || isFinishing || isStoring || isRemoving || isFetchingPdfs || isFetchingPdf || isConfirmingPdf || isConfirmingExport || isDownloading,
    /** Puts products in the jig (all of them when `slots` is omitted); `done = false` takes them back out. */
    load: (slots?: number[], done = true) => run(() => loadProducts({ id, slots, done }).unwrap()),
    start: () => run(() => startSession(id).unwrap()),
    finish: () => run(() => finishSession(id).unwrap()),
    /** Puts products back in their location (all of them when `slots` is omitted). */
    /** Takes a product out of the session (a mistake to fix, for example); resolves to true when the session got emptied. */
    remove: async (slot: number) => {
      try {
        const data = await removeProduct({ id, slot }).unwrap();
        return data === null;
      } catch (exception) {
        toast.error(getApiErrorMessage(exception));
        return false;
      }
    },
    /** Fetches (or fetches again) the PDF of one personalization from the shop. */
    fetchPdf: (customization: number) => run(() => fetchCustomizationPdf({ id, customization }).unwrap()),
    /** Fetches the missing PDFs of the session; tells the operator how many the shop does not have. */
    fetchMissingPdfs: () =>
      run(async () => {
        const { failed } = await fetchSessionPdfs(id).unwrap();
        if (failed > 0) {
          toast.warning(`${failed} PDF introuvable${failed > 1 ? 's' : ''} chez la boutique.`);
        }
      }),
    /** Moves on from the PDF step (a missing PDF does not block). */
    confirmPdf: () => run(() => confirmPdf(id).unwrap()),
    /** Moves on from the export step to the machine (downloading the zip is not required). */
    confirmExport: () => run(() => confirmExport(id).unwrap()),
    /** Downloads the zip of the production files. */
    download: () =>
      run(async () => {
        const { blob, filename } = await downloadExport(id).unwrap();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
      }),
    store: (slots?: number[], done = true) => run(() => storeProducts({ id, slots, done }).unwrap()),
  };
};
